"use client";
import React from "react";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";

const features = [
  {
    title: "Digital Answer Script Management",
    points: [
      "Secure upload & storage of scanned answer booklets",
      "High-resolution display with zoom/pan/rotate functions",
    ],
  },
  {
    title: "Intelligent Marking Interface",
    points: [
      "Customizable marking rubrics & scheme of evaluation",
      "Multi-colour annotation tools (highlighting, underlining, comments)",
    ],
  },
  {
    title: "Distributed Evaluation System",
    points: [
      "Automated script distribution to examiners",
      "Location independent marking (anytime-anywhere)",
      "Parallel evaluation by multiple markers",
    ],
  },
  {
    title: "Quality Assurance Controls",
    points: [
      "Seed script mechanism for standardization",
      "Double marking for critical assessments",
      "Real-time monitoring of examiner performance",
    ],
  },
  {
    title: "Comprehensive Analytics & Reporting",
    points: [
      "Instant mark calculation & aggregation",
      "Examiner productivity metrics",
      "Detailed discrepancy analysis reports",
    ],
  },
  {
    title: "Seamless System Integration",
    points: [
      "Compatibility with existing examination platforms",
      "API connectivity with student databases",
      "Bulk import/export of evaluation data",
    ],
  },
  {
    title: "Robust Security Framework",
    points: [
      "Encrypted data transmission & storage",
      "Role-based access permissions",
      "Complete audit trail of all marking actions",
    ],
  },
  {
    title: "Scalable Infrastructure",
    points: [
      "Cloud-based deployment options",
      "Capability to handle peak evaluation loads",
    ],
  },
];

const advantages = [
  "Efficiency and speed",
  "Quality evaluation and quick result generation",
  "Reduces cost related to manual evaluation",
  "Enhanced security (answer book integrity maintained, student details hidden)",
  "Scalability and flexibility (cloud-based, multi-evaluation mode)",
  "Accuracy and fairness ensured",
  "Data-driven insights with graphical dashboard",
  "Seamless integration with running systems",
];

const Onmark = () => {
  return (
    <div className="bg-white text-dark mr-50 ml-50">
      {/* Header Section */}
      <section className=" py-5 text-start">
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className=" fs-3 fw-bold text-primary"
        >
          Answer Book Scanning (Uncut) & Digital Evaluation
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-4 fs-5 text-black lh-lg"
        >
          Onmark solutions is designed to empower certification bodies with a
          secure, scalable, and intelligent platform that revolutionizes answer
          book scanning (uncut) and digital evaluation. Onmark’s scanning module
          consisted of pioneering solution where answer books scanning is done
          without cutting/de-threading. Onmark solution eliminates manual
          inefficiencies and ensures fraud-proof evaluation of answer books.
          Onmark solution comes with dynamic and client specific dashboard that
          facilitate real-time analytics to certification bodies gain actionable
          insights to enhance decision-making. Onmark solution is a cloud-based
          platform that supports home-based and/or centre-based and/or blended
          evaluation possibilities. By choosing our solution, clients benefit
          from faster results, reduced administrative burden, and unparalleled
          security.
        </motion.p>
      </section>

      {/* Features Section */}
      <section className=" py-5">
        <div className="row g-4">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              className="col-md-6 mt-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h5 className="card-title fw-semibold text-primary">
                    {feature.title}
                  </h5>
                  <ul className="list-unstyled mt-3">
                    {feature.points.map((point, i) => (
                      <li key={i} className="d-flex align-items-start mb-2">
                        <FaCheckCircle className="text-success me-2 mt-1" />
                        <span className="text-black" >{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Advantages */}
      <section
        className="text-white py-5 px-4"
        style={{ backgroundColor: "#0a1530" }}
      >
        <div className=" text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="fw-bold display-6"
          >
            Key Advantages
          </motion.h2>
          <div className="row mt-4 g-4 text-start">
            {advantages.map((adv, idx) => (
              <motion.div
                key={idx}
                className="col-sm-6 col-md-4"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="bg-white bg-opacity-10 p-3 rounded d-flex align-items-start">
                  <FaCheckCircle className="text-success me-2 mt-1" />
                  <span className="text-white">{adv}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Onmark;
